import { useState } from 'react';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import DatePicker from '@components/ui/DatePicker';
import Toggle from '@components/ui/Toggle';
import { useApiClient } from '@hooks/useApiClient';
import { toast } from '@utils/toast';
import { triggerStoreEvent } from '../../../../services/storeEventService';
import {
    ADMIN_TRIGGER_EVENTS,
    type AdminTriggerEventType,
} from '../../../../types/api.types';

interface SendEventTabProps {
    storeId: string;
    storeBrandId: string;
}

const ACTION_LABELS: Record<AdminTriggerEventType, string> = {
    FORCE_REFRESH_SALE_LIST: 'Refresh product list',
    REFRESH_SALE_CONFIG: 'Refresh sale settings',
    AUTO_RELOAD: 'Refresh store app',
    UPDATE_PRINTER_SERVICE: 'Refresh receipt printer',
    SYNC_ORDERS: 'Sync orders with system',
    SAVE_UNSYNCED_ORDERS: 'Save orders waiting to sync',
    PUSH_FRONTEND_LOGS: 'Upload app activity logs',
};

interface ConditionalFields {
    forceReload: boolean;
    batchSize: string;
    queue: string;
    fromDate: Date | undefined;
    toDate: Date | undefined;
}

const DEFAULT_CONDITIONAL: ConditionalFields = {
    forceReload: false,
    batchSize: '',
    queue: '',
    fromDate: undefined,
    toDate: undefined,
};

export default function SendEventTab({ storeId, storeBrandId }: SendEventTabProps) {
    const [selectedEvent, setSelectedEvent] = useState<AdminTriggerEventType>(
        ADMIN_TRIGGER_EVENTS.FORCE_REFRESH_SALE_LIST
    );
    const [fields, setFields] = useState<ConditionalFields>(DEFAULT_CONDITIONAL);

    const { loading, callApi, clearError } = useApiClient({ toast: true });

    const handleEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedEvent(e.target.value as AdminTriggerEventType);
        setFields(DEFAULT_CONDITIONAL);
        clearError();
    };

    const handleSend = async () => {
        clearError();

        const request: Parameters<typeof triggerStoreEvent>[0] = {
            eventName: selectedEvent,
            storeIds: [storeId],
        };

        if (selectedEvent === 'AUTO_RELOAD') {
            request.forceReload = fields.forceReload;
        }
        if (selectedEvent === 'SYNC_ORDERS') {
            if (fields.batchSize) request.batchSize = parseInt(fields.batchSize, 10);
        }
        if (selectedEvent === 'SAVE_UNSYNCED_ORDERS') {
            if (fields.batchSize) request.batchSize = parseInt(fields.batchSize, 10);
            if (fields.queue) request.queue = fields.queue;
        }
        if (selectedEvent === 'PUSH_FRONTEND_LOGS') {
            if (fields.fromDate) request.fromDate = fields.fromDate.toISOString();
            if (fields.toDate) request.toDate = fields.toDate.toISOString();
            if (fields.batchSize) request.batchSize = parseInt(fields.batchSize, 10);
        }

        await callApi(
            () => triggerStoreEvent(request, storeBrandId),
            (res) => {
                toast.success(res.message || 'Update sent to this store.');
            }
        );
    };

    return (
        <div className="space-y-5">
            <p className="text-sm text-[#64748b]">
                Send a one-time instruction to this store&apos;s app — for example, to refresh
                products, sync orders, or troubleshoot a technical issue.
            </p>

            <div>
                <label
                    htmlFor="action-select"
                    className="block text-sm font-medium text-[#1e293b] mb-2"
                >
                    Choose an action
                </label>
                <select
                    id="action-select"
                    value={selectedEvent}
                    onChange={handleEventChange}
                    className="w-full h-10 px-3 text-sm bg-[#f8fafc] border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-[#6e64f0]"
                >
                    {Object.values(ADMIN_TRIGGER_EVENTS).map((event) => (
                        <option key={event} value={event}>
                            {ACTION_LABELS[event]}
                        </option>
                    ))}
                </select>
            </div>

            {selectedEvent === 'AUTO_RELOAD' && (
                <div className="rounded-xl border border-[#e2e8f0] px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-sm font-medium text-[#1e293b]">Full refresh</p>
                            <p className="text-xs text-[#64748b] mt-0.5">
                                Clears cached data and reloads the app from scratch.
                            </p>
                        </div>
                        <Toggle
                            label="Full refresh"
                            value={fields.forceReload}
                            onChange={(v) => setFields((prev) => ({ ...prev, forceReload: v }))}
                        />
                    </div>
                </div>
            )}

            {selectedEvent === 'SYNC_ORDERS' && (
                <Input
                    label="Orders per batch (optional)"
                    type="number"
                    placeholder="e.g. 50"
                    value={fields.batchSize}
                    onChange={(e) => setFields((prev) => ({ ...prev, batchSize: e.target.value }))}
                />
            )}

            {selectedEvent === 'SAVE_UNSYNCED_ORDERS' && (
                <>
                    <Input
                        label="Orders per batch (optional)"
                        type="number"
                        placeholder="e.g. 50"
                        value={fields.batchSize}
                        onChange={(e) =>
                            setFields((prev) => ({ ...prev, batchSize: e.target.value }))
                        }
                    />
                    <Input
                        label="Processing queue (optional)"
                        type="text"
                        placeholder="Queue name"
                        value={fields.queue}
                        onChange={(e) =>
                            setFields((prev) => ({ ...prev, queue: e.target.value }))
                        }
                    />
                </>
            )}

            {selectedEvent === 'PUSH_FRONTEND_LOGS' && (
                <>
                    <DatePicker
                        label="From date"
                        value={fields.fromDate}
                        onChange={(date) => setFields((prev) => ({ ...prev, fromDate: date }))}
                        placeholder="Select start date"
                    />
                    <DatePicker
                        label="To date"
                        value={fields.toDate}
                        onChange={(date) => setFields((prev) => ({ ...prev, toDate: date }))}
                        placeholder="Select end date"
                    />
                    <Input
                        label="Logs per batch (optional)"
                        type="number"
                        placeholder="e.g. 50"
                        value={fields.batchSize}
                        onChange={(e) =>
                            setFields((prev) => ({ ...prev, batchSize: e.target.value }))
                        }
                    />
                </>
            )}

            <Button
                variant="primary"
                isLoading={loading}
                onClick={handleSend}
                className="w-full"
            >
                Send to this store
            </Button>
        </div>
    );
}
